/**
 * Component Card JavaScript
 * Handles variant button clicks and price updates on product cards
 */

class ComponentCard extends HTMLElement {
  connectedCallback() {
    const cardWrapper = this.closest('.card-wrapper') || this;
    bindCardVariantItems(cardWrapper);
  }
}

// Initialize component cards on page load and when DOM updates
if (!customElements.get('component-card')) {
  customElements.define('component-card', ComponentCard);
}

function getCardVariantInput(cardWrapper) {
  return (
    cardWrapper.querySelector('.card-add-to-cart form[data-type="add-to-cart-form"] input[name="id"]') ||
    cardWrapper.querySelector('.card-add-to-cart input[name="id"]')
  );
}

function getSelectedCardVariantId(cardWrapper) {
  const cardVariantsPrice = cardWrapper.querySelector('.card-variants-price');
  if (cardVariantsPrice) {
    const activeVariantItem = cardVariantsPrice.querySelector('.card-variant__item--active');
    if (activeVariantItem?.dataset.variantId) {
      return activeVariantItem.dataset.variantId;
    }
  }

  return getCardVariantInput(cardWrapper)?.value || null;
}

function selectCardVariant(cardWrapper, item) {
  const cardVariantsPrice = item.closest('.card-variants-price');
  if (!cardVariantsPrice) return;

  cardVariantsPrice.querySelectorAll('.card-variant__item[data-variant-id]').forEach((variantItem) => {
    const isSelected = variantItem === item;
    variantItem.classList.toggle('card-variant__item--active', isSelected);
    variantItem.setAttribute('aria-checked', isSelected ? 'true' : 'false');
  });

  const cardPriceElement = cardVariantsPrice.querySelector('.variant__price');
  const price = item.dataset.variantPrice;
  if (cardPriceElement && price) {
    cardPriceElement.textContent = price;
  }

  if (!item.dataset.variantId) return;

  updateCardVariantInput(cardWrapper, item.dataset.variantId);

  if (cardWrapper.dataset.visitorQuoteOnly === 'true') return;

  syncCardCtaAvailability(cardWrapper, item.dataset.variantAvailable === 'true');
}

function getCardCtaContainer(cardWrapper) {
  if (cardWrapper.classList.contains('product-card-wrapper--list')) {
    return cardWrapper.querySelector('.product-card__aside');
  }

  return cardWrapper.querySelector('.nof-card-content');
}

function getCardQuickAdd(cardWrapper) {
  const ctaContainer = getCardCtaContainer(cardWrapper);
  if (!ctaContainer) return null;

  return ctaContainer.querySelector('.quick-add.card-add-to-cart') || ctaContainer.querySelector('.quick-add');
}

function getCardCtaButtons(cardWrapper) {
  const quickAdd = getCardQuickAdd(cardWrapper);
  if (!quickAdd) {
    return { quickAdd: null, addToCartButton: null, addToQuoteButton: null };
  }

  return {
    quickAdd,
    addToCartButton: quickAdd.querySelector('.card-add-to-cart__button'),
    addToQuoteButton: quickAdd.querySelector('.add-to-quote__button'),
  };
}

function bindCardVariantRadiogroupKeydown(radiogroup, cardWrapper) {
  if (radiogroup.dataset.variantKeyboardInitialized) return;

  radiogroup.addEventListener('keydown', (event) => {
    const items = Array.from(radiogroup.querySelectorAll('.card-variant__item[data-variant-id]'));
    if (!items.length) return;

    const currentIndex = items.findIndex((item) => item.getAttribute('aria-checked') === 'true');
    const index = currentIndex === -1 ? 0 : currentIndex;
    let nextIndex = index;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (index + 1) % items.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (index - 1 + items.length) % items.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = items.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const nextItem = items[nextIndex];
    nextItem.focus();
    selectCardVariant(cardWrapper, nextItem);
  });

  radiogroup.dataset.variantKeyboardInitialized = 'true';
}

function bindCardVariantItems(cardWrapper) {
  cardWrapper.querySelectorAll('[role="radiogroup"].card-variants__wrapper').forEach((radiogroup) => {
    bindCardVariantRadiogroupKeydown(radiogroup, cardWrapper);
  });

  cardWrapper.querySelectorAll('.card-variant__item[data-variant-id]').forEach((item) => {
    if (item.dataset.variantClickInitialized) return;

    item.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      selectCardVariant(cardWrapper, item);
    });

    item.dataset.variantClickInitialized = 'true';
  });
}

function updateCardVariantInput(cardWrapper, variantId) {
  if (!variantId) return;

  const variantInput = getCardVariantInput(cardWrapper);
  if (!variantInput) return;

  variantInput.value = variantId;
  variantInput.dispatchEvent(new Event('change', { bubbles: true }));
}

let accountRequiredMarqueeResizeBound = false;
let accountRequiredMarqueeLoadBound = false;

function isMobileAccountRequiredMarquee() {
  return window.matchMedia('(max-width: 749px)').matches;
}

function accountRequiredShouldForceMarquee(button) {
  return Boolean(
    isMobileAccountRequiredMarquee() &&
      button.closest('.product-card-wrapper--list .product-card__aside')
  );
}

function accountRequiredTextOverflows(button) {
  const segment = button.querySelector('.card-add-to-cart__text-segment:not([aria-hidden="true"])');
  const container = button.querySelector('.card-add-to-cart__text');
  if (!segment || !container) return false;

  const containerWidth = container.getBoundingClientRect().width;
  if (containerWidth <= 0) return null;

  const segmentStyles = window.getComputedStyle(segment);
  const lineHeight = parseFloat(segmentStyles.lineHeight) || 0;
  if (lineHeight > 0 && segment.getBoundingClientRect().height > lineHeight * 1.45) {
    return true;
  }

  const probe = document.createElement('span');
  probe.textContent = segment.textContent;
  probe.setAttribute('aria-hidden', 'true');
  probe.style.cssText = [
    'position:absolute',
    'visibility:hidden',
    'pointer-events:none',
    'white-space:nowrap',
    'display:inline-block',
    `font:${segmentStyles.font}`,
    `font-size:${segmentStyles.fontSize}`,
    `font-weight:${segmentStyles.fontWeight}`,
    `letter-spacing:${segmentStyles.letterSpacing}`,
  ].join(';');
  container.appendChild(probe);
  const overflows = probe.getBoundingClientRect().width > containerWidth;
  probe.remove();
  return overflows;
}

function accountRequiredNeedsMarquee(button) {
  if (!isMobileAccountRequiredMarquee()) return false;
  if (accountRequiredShouldForceMarquee(button)) return true;

  const overflow = accountRequiredTextOverflows(button);
  return overflow === true;
}

function clearAccountRequiredMarquee(button) {
  button.classList.remove('account-required__button--marquee');
  button.style.removeProperty('--card-account-marquee-duration');

  const track = button.querySelector('.card-add-to-cart__text-track');
  if (!track) return;

  track.querySelectorAll('.card-add-to-cart__text-segment[aria-hidden="true"]').forEach((node) => {
    node.remove();
  });
}

let accountRequiredMarqueeRefreshing = false;

function refreshAccountRequiredMarquees() {
  if (accountRequiredMarqueeRefreshing) return;

  accountRequiredMarqueeRefreshing = true;

  try {
    document.querySelectorAll('.account-required__button').forEach((button) => {
      const track = button.querySelector('.card-add-to-cart__text-track');
      const segment = button.querySelector('.card-add-to-cart__text-segment:not([aria-hidden="true"])');
      const shouldMarquee = Boolean(track && segment && accountRequiredNeedsMarquee(button));
      const isMarquee = button.classList.contains('account-required__button--marquee');

      if (!shouldMarquee) {
        if (isMarquee) clearAccountRequiredMarquee(button);
        return;
      }

      if (isMarquee && track.querySelector('.card-add-to-cart__text-segment[aria-hidden="true"]')) {
        return;
      }

      clearAccountRequiredMarquee(button);

      const duplicate = segment.cloneNode(true);
      duplicate.setAttribute('aria-hidden', 'true');
      track.appendChild(duplicate);

      button.classList.add('account-required__button--marquee');

      const duration = Math.max(12, Math.min(24, track.scrollWidth / 2 / 16));
      button.style.setProperty('--card-account-marquee-duration', `${duration}s`);
    });

    bindAccountRequiredMarqueeObservers();
  } finally {
    accountRequiredMarqueeRefreshing = false;
  }
}

let accountRequiredMarqueeScheduleId = null;

function scheduleAccountRequiredMarqueeRefresh() {
  if (accountRequiredMarqueeScheduleId != null) return;

  accountRequiredMarqueeScheduleId = requestAnimationFrame(() => {
    accountRequiredMarqueeScheduleId = null;
    refreshAccountRequiredMarquees();

    requestAnimationFrame(() => {
      refreshAccountRequiredMarquees();
    });
  });
}

function bindAccountRequiredMarqueeResize() {
  if (accountRequiredMarqueeResizeBound) return;

  accountRequiredMarqueeResizeBound = true;
  window.addEventListener('resize', scheduleAccountRequiredMarqueeRefresh, { passive: true });
}

function bindAccountRequiredMarqueeLoad() {
  if (accountRequiredMarqueeLoadBound) return;

  accountRequiredMarqueeLoadBound = true;
  window.addEventListener('load', scheduleAccountRequiredMarqueeRefresh, { passive: true });

  if (document.fonts?.ready) {
    document.fonts.ready.then(scheduleAccountRequiredMarqueeRefresh).catch(() => {});
  }
}

function bindAccountRequiredMarqueeObservers() {
  if (typeof ResizeObserver === 'undefined') return;

  if (!window._accountRequiredMarqueeObserver) {
    window._accountRequiredMarqueeObserver = new ResizeObserver(() => {
      if (!accountRequiredMarqueeRefreshing) {
        scheduleAccountRequiredMarqueeRefresh();
      }
    });
  }

  document.querySelectorAll('.account-required__button').forEach((button) => {
    window._accountRequiredMarqueeObserver.observe(button);
  });
}

function initAccountRequiredMarquees() {
  bindAccountRequiredMarqueeResize();
  bindAccountRequiredMarqueeLoad();
  scheduleAccountRequiredMarqueeRefresh();
}

function isAccountRequiredMarqueeInternalNode(node) {
  return Boolean(
    node instanceof Element &&
      (node.matches('.card-add-to-cart__text-segment[aria-hidden="true"]') ||
        node.closest('.account-required__button .card-add-to-cart__text-track'))
  );
}

function nodeAddsAccountRequiredButtons(node) {
  if (!(node instanceof Element)) return false;

  if (node.matches('.account-required__button, .card-wrapper, .product-grid, .product-grid-container')) {
    return true;
  }

  return Boolean(node.querySelector('.account-required__button'));
}

function shouldReactToMarqueeMutation(mutations) {
  if (accountRequiredMarqueeRefreshing) return false;

  return mutations.some((mutation) => {
    if (mutation.type !== 'childList') return false;

    const target = mutation.target;
    if (target instanceof Element && target.closest('.account-required__button .card-add-to-cart__text-track')) {
      return false;
    }

    const addedElements = [...mutation.addedNodes].filter((node) => node instanceof Element);
    const removedElements = [...mutation.removedNodes].filter((node) => node instanceof Element);

    if (
      !addedElements.length &&
      removedElements.length &&
      removedElements.every((node) => isAccountRequiredMarqueeInternalNode(node))
    ) {
      return false;
    }

    return addedElements.some((node) => {
      if (isAccountRequiredMarqueeInternalNode(node)) return false;
      return nodeAddsAccountRequiredButtons(node);
    });
  });
}

// Also handle event delegation for cards that may be added dynamically
document.addEventListener('DOMContentLoaded', () => {
  initCardVariantButtons();
  initCardAddToCartButtons();
  initCardRfqButtons();
  initAccountRequiredMarquees();
});

function resyncCardCtaFromSelection(cardWrapper) {
  if (cardWrapper.dataset.visitorQuoteOnly === 'true') return;

  const activeVariantItem = cardWrapper.querySelector('.card-variant__item--active');
  if (!activeVariantItem) return;

  syncCardCtaAvailability(cardWrapper, activeVariantItem.dataset.variantAvailable === 'true');
}

function isQuoteOnlyCard(cardWrapper) {
  return (
    cardWrapper.dataset.visitorQuoteOnly === 'true' || cardWrapper.dataset.quoteOnly === 'true'
  );
}

function syncCardCtaAvailability(cardWrapper, isAvailable) {
  const { addToCartButton, addToQuoteButton } = getCardCtaButtons(cardWrapper);
  if (!addToCartButton || !addToQuoteButton) return;

  const buttonText = addToCartButton.querySelector('.card-add-to-cart__text');
  const soldOutMessage = addToCartButton.querySelector('.sold-out-message');
  const quoteOnly = isQuoteOnlyCard(cardWrapper);

  if (isAvailable && !quoteOnly) {
    addToCartButton.classList.remove('hidden');
    addToCartButton.disabled = false;
    addToCartButton.removeAttribute('aria-disabled');
    if (buttonText) buttonText.classList.remove('hidden');
    if (soldOutMessage) soldOutMessage.classList.add('hidden');
    addToQuoteButton.classList.add('hidden');
    return;
  }

  if (isAvailable && quoteOnly) {
    addToCartButton.disabled = true;
    addToCartButton.classList.add('hidden');
    addToCartButton.setAttribute('aria-disabled', 'true');
    if (buttonText) buttonText.classList.add('hidden');
    if (soldOutMessage) soldOutMessage.classList.add('hidden');
    addToQuoteButton.classList.remove('hidden');
    return;
  }

  addToCartButton.disabled = true;
  addToCartButton.classList.add('hidden');
  addToCartButton.setAttribute('aria-disabled', 'true');
  if (buttonText) buttonText.classList.add('hidden');
  if (soldOutMessage) soldOutMessage.classList.remove('hidden');
  addToQuoteButton.classList.remove('hidden');
}

function initCardRfqButtons() {
  document.querySelectorAll('.product-card-wrapper').forEach((cardWrapper) => {
    const ctaContainer = getCardCtaContainer(cardWrapper);
    if (!ctaContainer?.querySelector('.account-required__button')) return;

    cardWrapper.querySelectorAll('.rfq-collection-btn').forEach((injectedButton) => {
      const injectedContainer = injectedButton.closest('.rfq_btn_wrapper') || injectedButton;
      injectedContainer.remove();
    });

    ctaContainer
      .querySelectorAll('.rfq-collection-btn, .rfq_btn_wrapper, .add-to-quote__button.rfq-collection-btn')
      .forEach((element) => {
        const injectedContainer = element.closest('.rfq_btn_wrapper') || element;
        injectedContainer.remove();
      });
  });
}

function initCardVariantButtons() {
  document.querySelectorAll('.card-wrapper').forEach((cardWrapper) => {
    // Check if already initialized
    if (cardWrapper.dataset.variantButtonsInitialized) return;

    const variantItems = cardWrapper.querySelectorAll('.card-variant__item[data-variant-id]');

    if (!variantItems.length) {
      cardWrapper.dataset.variantButtonsInitialized = 'true';
      return;
    }

    bindCardVariantItems(cardWrapper);
    resyncCardCtaFromSelection(cardWrapper);

    cardWrapper.dataset.variantButtonsInitialized = 'true';
  });
}

// Handle add to cart buttons on product cards
function initCardAddToCartButtons() {
  document.querySelectorAll('.card-add-to-cart__button').forEach((button) => {
    // Check if already initialized
    if (button.dataset.addToCartInitialized) return;

    button.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Get the card wrapper
      const cardWrapper = button.closest('.card-wrapper');
      if (!cardWrapper) return;

      // Get selected variant ID
      const cardVariantsPrice = cardWrapper.querySelector('.card-variants-price');
      let variantId = button.dataset.defaultVariantId;
      
      if (cardVariantsPrice) {
        const activeVariantItem = cardVariantsPrice.querySelector('.card-variant__item--active');
        if (activeVariantItem?.dataset.variantId) {
          variantId = activeVariantItem.dataset.variantId;
        }
      }

      if (!variantId) return;

      // Prevent double submission
      if (button.getAttribute('aria-disabled') === 'true' || button.disabled) return;

      // Set loading state
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('loading');
      const spinner = button.querySelector('.loading__spinner');
      if (spinner) spinner.classList.remove('hidden');

      // Prepare form data
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);

      // Get cart drawer/notification for updates
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cart) {
        formData.append(
          'sections',
          cart.getSectionsToRender().map((section) => section.id)
        );
        formData.append('sections_url', window.location.pathname);
        cart.setActiveElement(document.activeElement);
      }

      // Configure fetch
      const config = typeof fetchConfig !== 'undefined' ? fetchConfig('javascript') : {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      };
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = formData;

      // Add to cart
      fetch(`${window.routes?.cart_add_url || '/cart/add.js'}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            // Error occurred
            const soldOutMessage = button.querySelector('.sold-out-message');
            if (soldOutMessage) {
              button.setAttribute('aria-disabled', true);
              const buttonText = button.querySelector('.card-add-to-cart__text');
              if (buttonText) buttonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
            }
            return;
          }

          // Success - update cart if available
          if (cart) {
            if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'card-product',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                cart.renderContents(response);
              });
            } else {
              cart.renderContents(response);
            }
          } else {
            // No cart drawer, redirect to cart page
            window.location = window.routes?.cart_url || '/cart';
          }
        })
        .catch((error) => {
          console.error('Error adding to cart:', error);
        })
        .finally(() => {
          button.classList.remove('loading');
          if (spinner) spinner.classList.add('hidden');
          // Only re-enable if not sold out
          const soldOutMessage = button.querySelector('.sold-out-message');
          if (!soldOutMessage || soldOutMessage.classList.contains('hidden')) {
            button.removeAttribute('aria-disabled');
          }
        });
    });

    button.dataset.addToCartInitialized = 'true';
  });
}

// Also run after DOM mutations for dynamically added content
if (typeof MutationObserver !== 'undefined') {
  let cardEnhancementTimer;

  const scheduleCardEnhancementInits = () => {
    clearTimeout(cardEnhancementTimer);
    cardEnhancementTimer = setTimeout(() => {
      initCardVariantButtons();
      initCardRfqButtons();
      initCardAddToCartButtons();
    }, 50);
  };

  const observer = new MutationObserver((mutations) => {
    const productGrid = document.getElementById('product-grid');
    if (productGrid) {
      const samitaInjected = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some(
          (node) =>
            node instanceof Element &&
            (node.matches?.('.rfq-collection-btn, .rfq_btn_wrapper') ||
              node.querySelector?.('.rfq-collection-btn'))
        )
      );

      if (samitaInjected) {
        initCardRfqButtons();
      }
    }

    scheduleCardEnhancementInits();

    if (shouldReactToMarqueeMutation(mutations)) {
      scheduleAccountRequiredMarqueeRefresh();
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
}

